// ---------------------------------------------------------------------------
// DatasetVersionField — pins the Data Lake source to "Latest" or the selected
// dataset's current version. The server reads `version === null` as "latest";
// a concrete number pins to that snapshot. We don't fetch full version history
// here — pinning to arbitrary older versions can come once the API lists them.
// ---------------------------------------------------------------------------

import { Dropdown, Field, Option } from '@fluentui/react-components';

import type { Dataset } from '../../../../../services/dataHubService.interface.js';

import { renderOptionText } from './datasetOption.js';

export function DatasetVersionField({
  selected,
  selectedId,
  selectedVersion,
  disabled,
  onSelect,
}: {
  selected: Dataset | undefined;
  selectedId: string | null;
  selectedVersion: number | null;
  disabled: boolean;
  onSelect: (
    selection: {
      id: string;
      version: number | null;
      displayName: string;
    } | null,
  ) => void;
}) {
  const displayName =
    selected !== undefined ? renderOptionText(selected) : (selectedId ?? '');

  return (
    <Field
      label="Version"
      hint="Leave as 'Latest' to always use the newest version at run time."
    >
      <Dropdown
        value={
          selectedVersion === null ? 'Latest' : `v${String(selectedVersion)}`
        }
        selectedOptions={[
          selectedVersion === null ? 'latest' : String(selectedVersion),
        ]}
        onOptionSelect={(_e, data) => {
          if (selectedId === null) return;
          if (data.optionValue === 'latest') {
            onSelect({ id: selectedId, version: null, displayName });
          } else if (data.optionValue !== undefined) {
            const v = Number(data.optionValue);
            onSelect({
              id: selectedId,
              version: Number.isFinite(v) ? v : null,
              displayName,
            });
          }
        }}
        disabled={disabled || selectedId === null}
        aria-label="Dataset version"
      >
        <Option value="latest" text="Latest">
          Latest
        </Option>
        {/* If the user has selected a dataset, surface its current
            version as a concrete option. */}
        {selected !== undefined && (
          <Option
            value={String(selected.version)}
            text={`v${String(selected.version)}`}
          >
            v{String(selected.version)} (current)
          </Option>
        )}
      </Dropdown>
    </Field>
  );
}
